import type { EntryCollection, EntrySkeletonType } from 'contentful'
import { middlewareGateway } from '~/gateway/middleware.gateway'

type ContentfulRequestHeaders = { 'Content-Type': 'application/json'; 'x-cntfl-response'?: 'slim' }

// https://www.contentful.com/developers/docs/javascript/tutorials/using-js-cda-sdk/
// https://www.contentful.com/developers/docs/references/content-delivery-api/#/reference/search-parameters/content-type
export const useContentful = () => {
  const getEntries = async (query: ContentfulParams & ContentfulPreviewParams) => {
    if (import.meta.server) {
      const skipPaths = [
        '/auth-redirect',
        '/favicon.ico',
        '/fonts',
        '/healthz',
        '/proxy',
        '/robots.txt',
        '/undefined',
        '/.well-known',
      ]
      const url = useRequestURL()
      const requestPath = url.pathname
      if (skipPaths.some((path) => requestPath.includes(path))) {
        return
      }
    }

    const { locale } = useNuxtApp().$i18n
    const queryWithDefaults = {
      locale: localeConfig[locale.value].code,
      include: 10,
      order: '-sys.updatedAt',
      'sys.publishedVersion[exists]': true,
      ...query,
    }

    const { isPreviewEnabled, previewConfig, sanitizeQueryForPreview, getPreviewContent } = useContentfulPreview()
    const queryParams = isPreviewEnabled ? sanitizeQueryForPreview(queryWithDefaults) : queryWithDefaults

    const safeSearchParams = toContentfulSearchParams(queryParams)
    const headers: ContentfulRequestHeaders = { 'Content-Type': 'application/json' }
    // Resolved links only, without `includes` or CMS bookkeeping (see cntfl.controller.ts). Preview
    // keeps the full response its filters were written against.
    if (!isPreviewEnabled) headers['x-cntfl-response'] = 'slim'
    const response = await middlewareGateway<EntryCollection<EntrySkeletonType, undefined, string>>(
      `/next-gen/cntfl/${isPreviewEnabled ? 'contentPreview' : 'content'}?${safeSearchParams.toString()}`,
      {
        headers,
        method: 'GET',
      },
    ).catch((e) => {
      return e instanceof Error ? e : new Error(String(e))
    })

    if (response instanceof Error) {
      console.error('CNTFL::Failed to fetch entries from middleware:', response)
      throw response
    }

    if (isPreviewEnabled) {
      return await getPreviewContent(response, previewConfig)
    }

    return response
  }

  return {
    getEntries,
  }
}
